import SweetAlert2 from "react-sweetalert2";
import React, {FC, useEffect, useState} from "react";
import {Box, Dialog, DialogContent, DialogTitle, Grid, Theme, Typography, useMediaQuery} from "@material-ui/core";
import ImageBlock from "./components/ImageBlock";
import {AccessibilityImages} from "../../../../../../../../../../../models/AccessibilityImages";
import SelectBlock from "./components/SelectBlock";
import {
  getTextByCurrentLanguage,
  range
} from "../../../../../../../../../../../utils/functions";
import {useSelector} from "../../../../../../../../../../../redux/store";
import {AppStateType} from "../../../../../../../../../../../redux/reducers";
import CheckboxBlock from "./components/CheckboxBlock";
import InputBlock from "./components/InputBlock";
import {GradientButton} from "../../../../../../../../../property-page-components/PropertyForm";
import {
  fetchUnitImagesByProperty,
  partialUpdateUnitImage,
  updateUnitImage
} from "../../../../../../../../../../../redux/actions/unitImagesActions";
import {createUnitImage} from "../../../../../../../../../../../services/imageService";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {CircularProgress, IconButton, useTheme} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "@emotion/styled";
import i18n from "i18next";

const TopBar = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    position: relative;
    .MuiButtonBase-root {
        width: 42px;
        color: rgba(18, 22, 25, 0.5);
    }
`

const initialData = {
  is_display_in_output_service: true,
  description: '',
  order: 0,
  accessibility_unit: '',
  accessibility: ''
}


type PropsType = {
  show: boolean;
  onClose: (accessibility_unit?: string | undefined) => void
  data?: AccessibilityImages | null
}

const ImageEditor: FC<PropsType> = ({show, onClose, data}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const theme = useTheme();
  const params: {id: string} = useParams()
  const user = useSelector((state) => state.authReducer.user);
  const [newImage, setNewImage] = useState<object | null>(null)
  const [unitsList, setUnitsList] = useState<{ key: string; title: string; }[]>([])
  const [editData, setEditData] = useState<any>(initialData)
  const images = useSelector((state: AppStateType) => state.unitImagesReducer.unitImagesByCompanyId)!
  const a11yesByProperty = useSelector((state: AppStateType) => state.a11yesReducer.a11yesByProperty)
  const units = useSelector((state: AppStateType) => state.unitsReducer.units)
  const [loading, setLoading] = useState<boolean>(false)
  const isNotDesktop = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (units.length > 0) {
      const mappedUnits = units.map((u) => ({key: u.id, title: getTextByCurrentLanguage(u.name)}))
      setUnitsList(mappedUnits)
    }
  }, [units])

  useEffect(() => {
    if (data) {
      setEditData({
        order: data?.order,
        description: data?.description,
        is_display_in_output_service: data?.is_display_in_output_service,
        accessibility_unit: data?.accessibility_unit,
        accessibility: data?.accessibility
      })
    }
  }, [data])

  const handleChange = (field: string, value: any, willClearField?: string | undefined) => {
    let newData = {
      ...editData,
      [field]: value,
    }

    if (willClearField) {
      newData = {
        ...newData,
        [willClearField]: ''
      }
    }

    setEditData(newData)
  }

  const getAccessibilityByUnit = (unitId: string) => {
    if (unitId) {
      return a11yesByProperty
        .filter((a11yes) => a11yes.accessibility_unit === unitId)
        .map((a11yes) => ({key: a11yes.id, title: `${a11yes.item_title} - ${a11yes.attribute_title}`}))
    }
    return []
  }

  const saveImageData = async () => {
    setLoading(true)
    if (data?.id) {
      // if we have existed image unit
      const foundIndexById = images.findIndex((i) => i.id == data.id)
      if (newImage) {
        await dispatch(partialUpdateUnitImage(foundIndexById, data.id, {image: newImage}, user!.token))
      }
      await dispatch(updateUnitImage(foundIndexById, data.id, editData, user!.token))
      await dispatch(fetchUnitImagesByProperty(user!.token, data.property))
      onClose(data.accessibility_unit)
    } else {
      // if we create new image
      const preparedData = {image: newImage, ...editData}
      if (preparedData.image) {
        await createUnitImage(preparedData, user!.token).then(async (result) => {
          await dispatch(fetchUnitImagesByProperty(user!.token, params.id))
          onClose(result.accessibility_unit)
        })
      }
    }
    setEditData({...initialData})
    setNewImage(null)
    setLoading(false)
  }


  return (
    <Dialog
      fullScreen={isNotDesktop}
      open={show}
      onClose={() => {
        setEditData(initialData)
        onClose(editData.accessibility_unit)
      }}
      fullWidth={true}
      maxWidth={'lg'}
      PaperProps={{
        style: {
          borderRadius: isNotDesktop ? 0 : '8px',
        },
      }}
    >
      <TopBar>
        <DialogTitle disableTypography>
          <Typography variant="h4" align="center">Image editor</Typography>
        </DialogTitle>
        <IconButton
          style={{position: 'absolute', top: '10px', right: '10px'}}
          edge="start"
          color="inherit"
          onClick={() => {
            setEditData(initialData)
            onClose(editData.accessibility_unit)
          }}
          aria-label="close"
        >
          <CloseIcon style={{color: 'gray'}}/>
        </IconButton>
      </TopBar>
      <DialogContent style={{padding: 24}}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={12} md={5}>
            <ImageBlock
              data={data}
              newImage={newImage}
              onSetNewImage={setNewImage}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={7}>
            <Box>
              <SelectBlock
                title={'accessibility_unit'}
                placeholder={'select_accessibility_unit'}
                list={unitsList}
                selected={editData.accessibility_unit}
                onHandleChange={handleChange}
                field={'accessibility_unit'}
                willClearField={'accessibility'}
                clearButton={false}
              />
            </Box>
            <Box>
              <SelectBlock
                title={'accessibility'}
                placeholder={'accessibility'}
                list={getAccessibilityByUnit(editData.accessibility_unit)}
                selected={editData.accessibility}
                onHandleChange={handleChange}
                field={'accessibility'}
              />
            </Box>
            <Box>
              <InputBlock
                title={'image_description'}
                type={'edit'}
                onHandleChange={handleChange}
                value={editData.description}
                field={'description'}
              />
            </Box>
            <Box>
              <CheckboxBlock
                title={'displayed_in_widget'}
                onHandleChange={handleChange}
                field={'is_display_in_output_service'}
                checked={editData.is_display_in_output_service}
              />
            </Box>
            <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
              <GradientButton
                disabled={loading || !newImage && !data || !editData.accessibility_unit}
                onClick={() => saveImageData()} style={{minWidth: '100px'}}
              >
                {t('propertyForm_save')}
                {loading ? (
                  <Box ml={1} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                    <CircularProgress size={15}/>
                  </Box>
                ) : null}
              </GradientButton>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default ImageEditor